import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase-config';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, Loader2, CheckCircle2, X } from 'lucide-react';

interface BrandingSettingsProps {
  customerId: string;
  currentBrandColor: string;
  currentAccentColor?: string | null;
  currentLogoUrl?: string | null;
  currentFaviconUrl?: string | null;
  currentBannerTitle?: string | null;
  currentBannerDescription?: string | null;
  onUpdate: () => void;
  onClose: () => void;
  isModal?: boolean;
}

export default function BrandingSettings({
  customerId,
  currentBrandColor,
  currentAccentColor,
  currentLogoUrl,
  currentFaviconUrl,
  currentBannerTitle,
  currentBannerDescription,
  onUpdate,
  onClose,
  isModal = true,
}: BrandingSettingsProps) {
  const [brandColor, setBrandColor] = useState(currentBrandColor);
  const [accentColor, setAccentColor] = useState(currentAccentColor || '');
  const [bannerTitle, setBannerTitle] = useState(currentBannerTitle || '');
  const [bannerDescription, setBannerDescription] = useState(currentBannerDescription || '');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(currentLogoUrl || null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(currentFaviconUrl || null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo must be less than 2MB');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (max 500KB for favicon)
      if (file.size > 500 * 1024) {
        setError('Favicon must be less than 500KB');
        return;
      }
      setFaviconFile(file);
      setFaviconPreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  const uploadFile = async (file: File, path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  };

  const handleSave = async () => {
    setUploading(true);
    setError(null);

    try {
      const updates: any = {
        brandColor,
        accentColor: accentColor || null,
        bannerTitle: bannerTitle || null,
        bannerDescription: bannerDescription || null,
      };

      // Upload logo if changed
      if (logoFile) {
        const logoUrl = await uploadFile(
          logoFile,
          `customers/${customerId}/logo.${logoFile.name.split('.').pop()}`
        );
        updates.logoUrl = logoUrl;
      }

      // Upload favicon if changed
      if (faviconFile) {
        const faviconUrl = await uploadFile(
          faviconFile,
          `customers/${customerId}/favicon.${faviconFile.name.split('.').pop()}`
        );
        updates.faviconUrl = faviconUrl;
      }

      // Update Firestore
      await updateDoc(doc(db, 'customers', customerId), updates);

      setSuccess(true);
      setTimeout(() => {
        onUpdate();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Error updating branding:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);

      // Show more specific error messages
      if (err.code === 'storage/unauthorized') {
        setError('Permission denied. You may not have access to upload files for this customer.');
      } else if (err.code === 'storage/canceled') {
        setError('Upload canceled.');
      } else if (err.code === 'storage/unknown') {
        setError('Unknown error occurred during upload. Check your connection.');
      } else if (err.code === 'permission-denied') {
        setError('Permission denied. Unable to update customer settings.');
      } else {
        setError(`Failed to update branding: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const content = (
    <div className={`bg-card border border-border rounded-lg ${isModal ? 'max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4' : 'w-full'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <h2 className="text-2xl font-semibold text-foreground">
          Branding Settings
        </h2>
        {isModal && (
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Success Message */}
          {success && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Branding updated successfully!</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Logo Upload */}
          <div className="space-y-2">
            <Label htmlFor="logo">Logo</Label>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={uploading}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Recommended: PNG or SVG, max 2MB
                </p>
              </div>
              {logoPreview && (
                <div className="w-24 h-24 border border-border rounded-lg flex items-center justify-center bg-muted overflow-hidden">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Favicon Upload */}
          <div className="space-y-2">
            <Label htmlFor="favicon">Favicon</Label>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <Input
                  id="favicon"
                  type="file"
                  accept="image/*"
                  onChange={handleFaviconChange}
                  disabled={uploading}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Recommended: 32x32 PNG or ICO, max 500KB
                </p>
              </div>
              {faviconPreview && (
                <div className="w-12 h-12 border border-border rounded-lg flex items-center justify-center bg-muted overflow-hidden">
                  <img
                    src={faviconPreview}
                    alt="Favicon preview"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Banner Title */}
          <div className="space-y-2">
            <Label htmlFor="bannerTitle">Banner Title</Label>
            <Input
              id="bannerTitle"
              type="text"
              value={bannerTitle}
              onChange={(e) => setBannerTitle(e.target.value)}
              placeholder="Building the future together"
              disabled={uploading}
            />
            <p className="text-xs text-muted-foreground">
              Optional headline shown at the top of your feedback portal
            </p>
          </div>

          {/* Banner Description */}
          <div className="space-y-2">
            <Label htmlFor="bannerDescription">Banner Description</Label>
            <textarea
              id="bannerDescription"
              value={bannerDescription}
              onChange={(e) => setBannerDescription(e.target.value)}
              placeholder="Your feedback shapes our product. Share your ideas and vote on features that matter to you."
              disabled={uploading}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              Optional description explaining your philosophy on product feedback
            </p>
          </div>

          {/* Brand Color */}
          <div className="space-y-2">
            <Label htmlFor="brandColor">Primary Brand Color</Label>
            <div className="flex gap-2">
              <Input
                id="brandColor"
                type="text"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                placeholder="#7C3AED"
                disabled={uploading}
                className="flex-1"
              />
              <input
                type="color"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                disabled={uploading}
                className="w-12 h-10 rounded border border-border cursor-pointer"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Used for primary actions and key brand elements
            </p>
          </div>

          {/* Accent Color */}
          <div className="space-y-2">
            <Label htmlFor="accentColor">Accent Color</Label>
            <div className="flex gap-2">
              <Input
                id="accentColor"
                type="text"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                placeholder="#14B8A6"
                disabled={uploading}
                className="flex-1"
              />
              <input
                type="color"
                value={accentColor || '#14B8A6'}
                onChange={(e) => setAccentColor(e.target.value)}
                disabled={uploading}
                className="w-12 h-10 rounded border border-border cursor-pointer"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Used for links, categories, and secondary elements (optional)
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={uploading || success}
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : success ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Saved!
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        {content}
      </div>
    );
  }

  return content;
}
